import React, { useCallback, useMemo, useState } from "react";
import ProductImage from "./ProductImage";
import { formatCHF } from "../../utils/formatCHF";
import ProductQuickViewModal from "../Modals/ProductQuickViewModal";

/**
 * ============================================================
 * ProductCard.jsx
 * ============================================================
 *
 * TEMPLATE MAINTENEUR — GUIDE MANUEL
 * ------------------------------------------------------------
 * RÔLE DE CE COMPOSANT
 * ------------------------------------------------------------
 * Cette carte produit :
 * - affiche un produit catalogue
 * - montre son pricing courant
 * - permet un ajout rapide au panier
 * - ouvre une quick view pour les produits au poids
 *
 * BUG MÉTIER TRAITÉ DANS CE PATCH
 * ------------------------------------------------------------
 * Cas réel Marché Deluxe :
 *
 * - visiteur ajoute un produit au panier à 9 CHF
 * - puis il se connecte
 * - le panier doit passer à 7 CHF
 *
 * Pour permettre cela, le panier doit recevoir un item
 * suffisamment riche en données pricing :
 *
 * - price              -> prix affiché maintenant
 * - displayPrice       -> idem
 * - appliedPrice       -> idem
 * - basePrice          -> prix public / visiteur
 * - publicPrice        -> alias clair
 * - memberPrice        -> prix membre / connecté
 * - old_price          -> prix barré / référence
 * - pricePro           -> compat legacy membre
 * - prix_connecte      -> compat legacy membre
 * - prix_membre        -> compat legacy membre
 * - pricingMode        -> mode courant du moteur pricing
 *
 * IMPORTANT
 * ------------------------------------------------------------
 * Ce composant ne doit PAS :
 * - casser la logique poids / pièces / unités fixes
 * - casser la quick view
 * - inventer une logique pricing différente du moteur
 *
 * Il doit simplement :
 * - transmettre au panier une structure enrichie
 * - rester compatible avec les anciens flows
 *
 * ============================================================
 */

/**
 * ============================================================
 * GUIDE — NORMALISATION CENTIMES
 * ============================================================
 *
 * Objectif :
 * - accepter int / float / string
 * - toujours sortir des centimes
 * - rester compatible legacy
 *
 * Exemples :
 * - 900  -> 900
 * - 9    -> 900
 * - 7.5  -> 750
 */
const toCents = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;

  if (Number.isInteger(n) && n >= 100) {
    return Math.round(n);
  }

  return Math.round(n * 100);
};

/**
 * ============================================================
 * GUIDE — NORMALISATION TEXTE UNITÉ
 * ============================================================
 *
 * Ce bloc évite les divergences entre :
 * - Bouteille
 * - bouteille
 * - bouteille 75cl
 * - boîte / boite
 */
const normalizeUnitText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/**
 * ============================================================
 * GUIDE — DÉTECTION MODE UNITÉ
 * ============================================================
 *
 * Permet de savoir si le produit est vendu :
 * - à l’unité métier (pièce, bouteille, pot, boîte...)
 * - par unité fixe (ex: 200g)
 * - au poids libre
 *
 * IMPORTANT
 * ------------------------------------------------------------
 * Ce bloc corrige le faux classement :
 * - "bouteille" -> au poids
 * - "pot"       -> au poids
 * - "boîte"     -> au poids
 */
const isPieceUnit = (unite) => {
  const u = normalizeUnitText(unite);

  return (
    u === "pièce" ||
    u === "pièces" ||
    u === "piece" ||
    u === "pieces" ||
    u === "unité" ||
    u === "unite" ||
    u === "unit" ||
    u === "units" ||
    u === "pcs" ||
    u === "bouteille" ||
    u === "bouteilles" ||
    u === "pot" ||
    u === "pots" ||
    u === "boîte" ||
    u === "boite" ||
    u === "boîtes" ||
    u === "boites" ||
    u === "bocal" ||
    u === "bocaux" ||
    u === "flacon" ||
    u === "flacons" ||
    u === "barquette" ||
    u === "barquettes" ||
    u === "pack" ||
    u === "packs" ||
    u.includes("pièce") ||
    u.includes("piece") ||
    u.includes("unité") ||
    u.includes("unite") ||
    u.includes("bouteille") ||
    u.includes("pot") ||
    u.includes("boîte") ||
    u.includes("boite") ||
    u.includes("bocal") ||
    u.includes("flacon") ||
    u.includes("barquette") ||
    u.includes("pack")
  );
};

/**
 * ============================================================
 * GUIDE — PARSE UNITÉ FIXE EN GRAMMES
 * ============================================================
 *
 * Exemples :
 * - "200g"        => 200
 * - "0.5kg"       => 500
 * - "190 grammes" => 190
 *
 * Si rien de fixe n’est détecté => null
 */
const parseFixedGramsFromUnit = (unite) => {
  const u = normalizeUnitText(unite).replace(",", ".");

  const m =
    u.match(/(\d+(\.\d+)?)\s*(kg|g)\b/) ||
    u.match(/(\d+(\.\d+)?)\s*grammes?\b/);

  if (!m) return null;

  const value = Number(m[1]);
  const unit = m[3] || "grammes";

  if (!Number.isFinite(value) || value <= 0) return null;

  if (unit === "kg") {
    return Math.round(value * 1000);
  }

  return Math.round(value);
};

/**
 * ============================================================
 * GUIDE — OPTIONS DE QUANTITÉ
 * ============================================================
 *
 * Ce bloc alimente le sélecteur inline.
 */
const qtyOptions = (min = 1, max = 10) =>
  Array.from({ length: max - min + 1 }, (_, i) => i + min);

/**
 * ============================================================
 * GUIDE — LIBELLÉ D’UNITÉ POUR L’AFFICHAGE
 * ============================================================
 *
 * Ce bloc garde le wording métier tel qu’attendu dans les cartes :
 * - bouteille
 * - pot
 * - boîte
 * - bocal
 * - unité / pièce
 */
const getDisplayUnitLabel = (unite) => {
  const raw = String(unite || "").trim();
  const u = normalizeUnitText(unite);

  if (u === "bouteille" || u === "bouteilles") return "Bouteille";
  if (u === "pot" || u === "pots") return "Pot";
  if (u === "boîte" || u === "boite" || u === "boîtes" || u === "boites")
    return "Boîte";
  if (u === "bocal" || u === "bocaux") return "Bocal";
  if (u === "flacon" || u === "flacons") return "Flacon";
  if (u === "barquette" || u === "barquettes") return "Barquette";
  if (u === "pack" || u === "packs") return "Pack";
  if (u === "pièce" || u === "pièces" || u === "piece" || u === "pieces") {
    return "Pièce";
  }
  if (u === "unité" || u === "unite" || u === "unit" || u === "units") {
    return "Unité";
  }

  return raw || "Pièce";
};

/**
 * ============================================================
 * GUIDE — LIBELLÉ QUANTITÉ COURT
 * ============================================================
 *
 * Ce bloc améliore le wording du sélecteur dans la carte.
 */
const getQtyLabel = ({ isWeightMode, isFixedGramUnit, isUnitPieceMode, t }) => {
  if (isWeightMode) return t?.chooseWeightShort || "Poids";
  if (isFixedGramUnit) return t?.unitsShort || "Unités";
  if (isUnitPieceMode) return t?.unitsShort || "Unités";
  return t?.unitsShort || "Unités";
};

/**
 * ============================================================
 * GUIDE — LIBELLÉ OPTION DE QUANTITÉ
 * ============================================================
 *
 * Ce bloc évite :
 * - "2 pièces" pour une bouteille
 * - "3 pièces" pour un pot
 * - "1 pièce" pour une boîte
 */
const getQtyOptionLabel = ({
  n,
  unitLabel,
  isFixedGramUnit,
  isUnitPieceMode,
}) => {
  const displayUnit = getDisplayUnitLabel(unitLabel);

  if (isFixedGramUnit) {
    return `${n} × ${unitLabel}`;
  }

  if (isUnitPieceMode) {
    const singular = displayUnit.toLowerCase();

    if (n > 1) {
      if (singular === "bouteille") return `${n} bouteilles`;
      if (singular === "pot") return `${n} pots`;
      if (singular === "boîte") return `${n} boîtes`;
      if (singular === "bocal") return `${n} bocaux`;
      if (singular === "flacon") return `${n} flacons`;
      if (singular === "barquette") return `${n} barquettes`;
      if (singular === "pack") return `${n} packs`;
      if (singular === "pièce") return `${n} pièces`;
      if (singular === "unité") return `${n} unités`;
      return `${n} ${singular}s`;
    }

    return `${n} ${singular}`;
  }

  return `${n} unité${n > 1 ? "s" : ""}`;
};

/**
 * ============================================================
 * GUIDE — NORMALISATION TYPE DE LAIT
 * ============================================================
 */
const getMilkTypeLabel = (value) => {
  const v = String(value || "").trim();
  return v || "";
};

/**
 * ============================================================
 * GUIDE — BUILD ITEM PANIER ENRICHI
 * ============================================================
 *
 * OBJECTIF
 * ------------------------------------------------------------
 * Transformer le produit UI courant en item panier robuste,
 * en injectant explicitement les sources pricing nécessaires
 * au re-pricing futur après login/logout.
 *
 * IMPORTANT
 * ------------------------------------------------------------
 * On ne dépend pas uniquement de `price`.
 * On transporte aussi :
 * - basePrice / publicPrice
 * - memberPrice
 * - old_price
 * - pricePro / prix_connecte / prix_membre
 * - pricingMode
 *
 * Ainsi, PanierContext et priceAuditor ont de quoi retravailler
 * proprement la ligne panier plus tard.
 */
const buildRichCartItem = ({ product, audit, qty, grams = 0, unitLabel }) => {
  const safeQty = Math.max(1, Number(qty) || 1);
  const safeGrams = Math.max(0, Number(grams) || 0);

  const displayPrice =
    toCents(audit?.displayPrice) ??
    toCents(product?.displayPrice) ??
    toCents(product?.price) ??
    0;

  const basePrice =
    toCents(audit?.basePrice) ??
    toCents(audit?.displayBasePrice) ??
    toCents(product?.basePrice) ??
    toCents(product?.publicPrice) ??
    toCents(product?.old_price) ??
    toCents(product?.oldPrice) ??
    toCents(product?.price) ??
    displayPrice;

  const memberPrice =
    toCents(audit?.targetPrice) ??
    toCents(audit?.memberPrice) ??
    toCents(product?.memberPrice) ??
    toCents(product?.pricePro) ??
    toCents(product?.prix_connecte) ??
    toCents(product?.prix_membre) ??
    null;

  const oldPrice =
    toCents(audit?.displayBasePrice) ??
    toCents(product?.old_price) ??
    toCents(product?.oldPrice) ??
    (memberPrice !== null && memberPrice < basePrice ? basePrice : null);

  return {
    ...product,

    qty: safeQty,
    grams: safeGrams,
    unitLabel: unitLabel || product?.unite || "pièce",

    price: displayPrice,
    displayPrice,
    appliedPrice: displayPrice,

    basePrice,
    publicPrice: basePrice,
    memberPrice,
    old_price: oldPrice,

    pricePro:
      toCents(product?.pricePro) ??
      toCents(product?.memberPrice) ??
      toCents(audit?.targetPrice),

    prix_connecte:
      toCents(product?.prix_connecte) ??
      toCents(product?.memberPrice) ??
      toCents(product?.pricePro) ??
      toCents(audit?.targetPrice),

    prix_membre:
      toCents(product?.prix_membre) ??
      toCents(product?.memberPrice) ??
      toCents(product?.pricePro) ??
      toCents(audit?.targetPrice),

    pricingMode: audit?.pricingMode || product?.pricingMode || "PUBLIC",
    audit,
  };
};

export default function ProductCard({
  product,
  audit,
  t,
  vueActuelle,
  ajouterAuPanier,
  isProfilReady,
  onProductClick,
}) {
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [justAdded, setJustAdded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  /**
   * ============================================================
   * GUIDE — OUVERTURE MODALE
   * ============================================================
   */
  const openQuickView = useCallback(() => {
    if (!product?.id) return;
    setIsQuickViewOpen(true);
    onProductClick?.(product);
  }, [product, onProductClick]);

  const closeQuickView = useCallback(() => setIsQuickViewOpen(false), []);

  /**
   * Skeleton tant que le profil / pricing n’est pas prêt
   */
  if (!isProfilReady) return <div className="product-card skeleton"></div>;

  /**
   * ============================================================
   * GUIDE — ÉTAT PRODUIT VENDABLE
   * ============================================================
   */
  const isOnline =
    Number(product?.isOnline ?? product?.is_online ?? 0) === 1 ||
    product?.isOnline === true;

  const stock = Number(product?.stock ?? 0) || 0;
  const canAddToCart = isOnline && stock > 0;

  /**
   * ============================================================
   * GUIDE — UNITÉ / MODES DE VENTE
   * ============================================================
   */
  const unitLabel = String(product?.unite || "pièce").trim();

  const isUnitPieceMode = useMemo(() => isPieceUnit(unitLabel), [unitLabel]);

  const fixedGrams = useMemo(
    () => parseFixedGramsFromUnit(unitLabel),
    [unitLabel]
  );

  const isFixedGramUnit = useMemo(() => {
    return !isUnitPieceMode && Number.isFinite(fixedGrams) && fixedGrams > 0;
  }, [isUnitPieceMode, fixedGrams]);

  const isWeightMode = useMemo(
    () => !isUnitPieceMode && !isFixedGramUnit,
    [isUnitPieceMode, isFixedGramUnit]
  );

  const qtyLabel = useMemo(() => {
    return getQtyLabel({
      isWeightMode,
      isFixedGramUnit,
      isUnitPieceMode,
      t,
    });
  }, [isWeightMode, isFixedGramUnit, isUnitPieceMode, t]);

  const qtyOpts = useMemo(() => qtyOptions(1, 10), []);

  /**
   * ============================================================
   * GUIDE — TEXTE PRODUIT / MODALE
   * ============================================================
   */
  const storyText = product?.histoire ?? product?.storytelling ?? "";

  /**
   * ============================================================
   * GUIDE — PRODUIT ENVOYÉ À LA QUICK VIEW
   * ============================================================
   *
   * IMPORTANT
   * ------------------------------------------------------------
   * La quick view doit recevoir un produit suffisamment riche
   * en pricing pour que son retour onAddToCart puisse reconstruire
   * un item panier cohérent.
   */
  const modalProduct = useMemo(() => {
    if (!product) return null;

    const imageUrl =
      product.imageUrl ||
      product.image ||
      product.img ||
      product.photo ||
      product.image_path ||
      product.imagePath ||
      product.urlImage ||
      "";

    return {
      ...product,
      imageUrl,
  lait_animal: product?.lait_animal || "",
  storytelling: storyText,
  histoire: storyText,
      price: toCents(audit?.displayPrice) ?? toCents(product?.price) ?? 0,
      displayPrice:
        toCents(audit?.displayPrice) ?? toCents(product?.displayPrice) ?? 0,
      appliedPrice:
        toCents(audit?.displayPrice) ?? toCents(product?.appliedPrice) ?? 0,
      basePrice:
        toCents(audit?.basePrice) ??
        toCents(audit?.displayBasePrice) ??
        toCents(product?.basePrice) ??
        toCents(product?.old_price) ??
        toCents(product?.price) ??
        0,
      publicPrice:
        toCents(audit?.basePrice) ??
        toCents(product?.publicPrice) ??
        toCents(product?.basePrice) ??
        toCents(product?.old_price) ??
        toCents(product?.price) ??
        0,
      memberPrice:
        toCents(audit?.targetPrice) ??
        toCents(product?.memberPrice) ??
        toCents(product?.pricePro) ??
        null,
      old_price:
        toCents(audit?.displayBasePrice) ??
        toCents(product?.old_price) ??
        toCents(product?.oldPrice),
      pricePro:
        toCents(product?.pricePro) ??
        toCents(product?.memberPrice) ??
        toCents(audit?.targetPrice),
      prix_connecte:
        toCents(product?.prix_connecte) ??
        toCents(product?.memberPrice) ??
        toCents(product?.pricePro) ??
        toCents(audit?.targetPrice),
      prix_membre:
        toCents(product?.prix_membre) ??
        toCents(product?.memberPrice) ??
        toCents(product?.pricePro) ??
        toCents(audit?.targetPrice),
      pricingMode: audit?.pricingMode || product?.pricingMode || "PUBLIC",
      audit,
      unite: unitLabel,
    };
  }, [product, storyText, audit, unitLabel]);

  /**
   * ============================================================
   * GUIDE — PRICING UI
   * ============================================================
   */
  const currentPrice = audit?.displayPrice ?? product?.price ?? 0;
  const oldPrice = audit?.displayBasePrice ?? null;
  const savingsTag = audit?.displaySavingsTag || "";
  const milkTypeLabel = getMilkTypeLabel(product?.type_lait);

  const unitDisplayLabel = useMemo(() => {
    if (isWeightMode) {
      return `${unitLabel} · ${t?.soldByWeight || "au poids"}`;
    }

    return getDisplayUnitLabel(unitLabel);
  }, [isWeightMode, unitLabel, t]);

  /**
   * ============================================================
   * GUIDE — AJOUT RAPIDE PANIER
   * ============================================================
   *
   * IMPORTANT
   * ------------------------------------------------------------
   * Ce bloc doit enrichir le produit avant envoi au panier.
   *
   * But :
   * - le panier ne doit pas recevoir seulement "price"
   * - il doit recevoir les sources pricing complètes
   */
  const handleInlineAdd = useCallback(() => {
    if (!canAddToCart || justAdded) return;

    const safeQty = Math.max(1, Number(qty) || 1);

    if (isWeightMode) {
      setIsQuickViewOpen(true);
      return;
    }

    const enriched = buildRichCartItem({
      product,
      audit,
      qty: safeQty,
      grams: 0,
      unitLabel: isFixedGramUnit ? unitLabel : unitLabel || "pièce",
    });

    ajouterAuPanier?.(enriched, safeQty, 0);
    setJustAdded(true);

    window.setTimeout(() => {
      setJustAdded(false);
    }, 1200);
  }, [
    canAddToCart,
    justAdded,
    qty,
    product,
    audit,
    ajouterAuPanier,
    isWeightMode,
    isFixedGramUnit,
    unitLabel,
  ]);

  return (
    <>
      <div
        className={`product-card mode-${String(
          audit?.pricingMode || "STANDARD"
        ).toLowerCase()} ${
          audit?.pricingMode === "MEMBER_ACTIVE" ? "action-premium" : ""
        }`}
        style={{
          ...styles.card,
          ...(isHovered ? styles.cardHover : null),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="product-image-container" style={styles.imageContainer}>
          {/* =====================================================
              BADGE SÉLECTION
              ===================================================== */}
          {audit?.pricingMode === "MEMBER_ACTIVE" && (
            <div
              className="luxury-badge"
              style={{
                ...styles.luxBadge,
                ...(isHovered ? styles.luxBadgeHover : null),
              }}
            >
              <span className="badge-text" style={styles.badgeText}>
                {t?.specialOffer || "Sélection"}
              </span>
            </div>
          )}

          {/* =====================================================
              BADGE TYPE DE LAIT
              ===================================================== */}
          {milkTypeLabel ? (
            <div style={styles.milkBadge}>
              <span style={styles.milkBadgeDot} />
              <span>{milkTypeLabel}</span>
            </div>
          ) : null}

          <button
            type="button"
            onClick={openQuickView}
            className="product-image-clickable"
            style={styles.imageButton}
            aria-label={`Voir ${product?.name}`}
            title={product?.name}
          >
            <ProductImage product={product} className="product-image-img" />
            <div style={styles.imageOverlay} />

            <div
              style={{
                ...styles.imageCta,
                ...(isHovered ? styles.imageCtaHover : null),
              }}
            >
              <span style={styles.imageCtaPill}>{t?.see || "Voir"}</span>
            </div>
          </button>
        </div>

        <div className="product-info" style={styles.infoBlock}>
          {/* =====================================================
              TITRE
              ===================================================== */}
          <h3 className="product-name" style={styles.productName}>
            {product?.name}
          </h3>

          {/* =====================================================
    BLOC PRIX — VERSION APAISÉE
    ===================================================== */}
<div className="price-stack" style={styles.priceStack}>
  <div className="current-price-wrapper" style={styles.currentPriceRow}>
    <span className="price-main" style={styles.priceMain}>
      {formatCHF(currentPrice)}
    </span>

    {audit?.displayUpsell ? (
      <span style={styles.memberPriceInline}>
        🔒 {t?.[audit.displayUpsell.i18nLabel] || "Prix membre"}{" "}
        {formatCHF(audit.displayUpsell.priceValue)}
      </span>
    ) : null}

    {oldPrice ? (
      <span style={styles.oldPriceInline}>
        {t?.insteadOf || "Au lieu de"}{" "}
        <span style={styles.oldPriceValue}>{formatCHF(oldPrice)}</span>
      </span>
    ) : null}

    {savingsTag ? (
      <span className="savings-tag" style={styles.savingsTag}>
        {savingsTag}
      </span>
    ) : null}
  </div>

  <div style={styles.metaRow}>
    <span className="product-unit" style={styles.unitPill}>
      {unitDisplayLabel}
    </span>
  </div>
</div>

          {/* =====================================================
              ACTIONS PANIER
              ===================================================== */}
          {vueActuelle !== "produits" && canAddToCart ? (
            <div style={styles.addRow}>
              {isWeightMode ? (
                <button
                  type="button"
                  className="add-btn-premium"
                  onClick={() => setIsQuickViewOpen(true)}
                  style={styles.addBtnWide}
                >
                  {t?.chooseWeight || "Choisir le poids"}
                </button>
              ) : (
                <>
                  <div style={styles.qtyWrap}>
                    <div style={styles.qtyLabel}>{qtyLabel}</div>

                    <select
                      value={qty}
                      onChange={(e) => setQty(Number(e.target.value))}
                      style={styles.qtySelect}
                      aria-label="Quantité"
                    >
                      {qtyOpts.map((n) => (
                        <option key={n} value={n}>
                          {getQtyOptionLabel({
                            n,
                            unitLabel,
                            isFixedGramUnit,
                            isUnitPieceMode,
                          })}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    className="add-btn-premium"
                    onClick={handleInlineAdd}
                    type="button"
                    style={{
                      ...styles.addBtn,
                      ...(justAdded ? styles.addBtnAdded : {}),
                    }}
                    title={t?.addCart || "Ajouter au panier"}
                    disabled={justAdded}
                  >
                    {justAdded ? "✓ AJOUTÉ" : t?.addCart || "Ajouter"}
                  </button>
                </>
              )}
            </div>
          ) : (
            <span className="info-badge" style={styles.infoBadge}>
              {!isOnline
                ? t?.showcaseOnly || "Vitrine"
                : stock <= 0
                ? t?.soldOut || "Rupture"
                : t?.showcaseOnly || "Vitrine"}
            </span>
          )}
        </div>
      </div>

      <ProductQuickViewModal
        isOpen={isQuickViewOpen}
        onClose={closeQuickView}
        product={modalProduct}
        minGrams={100}
        maxGrams={2000}
        stepGrams={100}
        onAddToCart={(payload) => {
          /**
           * =====================================================
           * GUIDE — RETOUR QUICK VIEW -> PANIER
           * =====================================================
           *
           * Ce retour doit produire le même niveau d’enrichissement
           * pricing que l’ajout inline.
           */
          if (payload?.grams) {
            const g = Number(payload.grams) || 0;
            if (g <= 0) return;

            const enriched = buildRichCartItem({
              product,
              audit,
              qty: 1,
              grams: g,
              unitLabel: `${g}g`,
            });

            ajouterAuPanier?.(enriched, 1, g);
            return;
          }

          const pieces = Math.max(1, Number(payload?.pieces || 0) || 1);

          const enriched = buildRichCartItem({
            product,
            audit,
            qty: pieces,
            grams: 0,
            unitLabel: unitLabel || "pièce",
          });

          ajouterAuPanier?.(enriched, pieces, 0);
        }}
      />
    </>
  );
}

const styles = {
  /**
   * ============================================================
   * CARTE GLOBALE
   * ============================================================
   *
   * On réduit les effets trop visibles.
   */
  card: {
    transition: "transform 160ms ease, box-shadow 160ms ease",
  },
  cardHover: {
    transform: "translateY(-1px)",
  },

  /**
   * ============================================================
   * IMAGE / OVERLAY
   * ============================================================
   */
  imageContainer: {
    position: "relative",
    borderRadius: 16,
    overflow: "hidden",
    background: "#f8f5ef",
    boxShadow: "0 8px 22px rgba(0,0,0,0.06)",
    border: "1px solid rgba(212,175,55,0.14)",
  },
  imageButton: {
    border: "none",
    background: "transparent",
    padding: 0,
    width: "100%",
    cursor: "pointer",
    position: "relative",
    display: "block",
  },
  imageOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.06))",
    pointerEvents: "none",
  },
  imageCta: {
    position: "absolute",
    right: 10,
    bottom: 10,
    pointerEvents: "none",
    transform: "translateY(3px)",
    opacity: 0.92,
    transition: "all 160ms ease",
  },
  imageCtaHover: {
    transform: "translateY(0)",
    opacity: 1,
  },
  imageCtaPill: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.1,
    background: "rgba(22,18,14,0.58)",
    color: "white",
    border: "1px solid rgba(255,255,255,0.12)",
    backdropFilter: "blur(4px)",
  },

  /**
   * ============================================================
   * BADGE SÉLECTION
   * ============================================================
   *
   * Devient discret, éditorial, compact.
   */
  luxBadge: {
    position: "absolute",
    top: 10,
    left: 10,
    zIndex: 3,
    padding: "5px 9px",
    borderRadius: 999,
    background: "rgba(48,39,25,0.72)",
    color: "#f5eee2",
    border: "1px solid rgba(212,175,55,0.22)",
    boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
    transition: "all 160ms ease",
    backdropFilter: "blur(4px)",
  },
  luxBadgeHover: {
    background: "rgba(48,39,25,0.78)",
  },
  badgeText: {
    position: "relative",
    zIndex: 2,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.12,
    textTransform: "uppercase",
  },

  /**
   * ============================================================
   * BADGE TYPE DE LAIT
   * ============================================================
   *
   * Le badge doit rester informatif sans casser l’univers luxe.
   */
  milkBadge: {
    position: "absolute",
    right: 10,
    top: 10,
    zIndex: 3,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 8px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.86)",
    color: "#6a583a",
    fontSize: 10,
    fontWeight: 600,
    border: "1px solid rgba(212,175,55,0.16)",
    boxShadow: "0 3px 8px rgba(0,0,0,0.04)",
    backdropFilter: "blur(6px)",
  },
  milkBadgeDot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "#cfad45",
    flexShrink: 0,
  },

  /**
   * ============================================================
   * INFOS PRODUIT
   * ============================================================
   *
   * On resserre les espaces pour éviter l’effet
   * trop étalé / fatigant sur mobile.
   */
  infoBlock: {
    display: "grid",
    gap: 8,
    paddingTop: 2,
  },
  productName: {
    margin: 0,
    lineHeight: 1.14,
    color: "#2b241d",
    fontWeight: 500,
    letterSpacing: "0.01em",
    fontSize: "clamp(1rem, 1.15vw, 1.18rem)",
  },

  /**
   * ============================================================
   * PRICING CLEAN — VERSION TRÈS ADOUCIE
   * ============================================================
   */
  priceStack: {
    display: "grid",
    gap: 6,
  },
  oldPriceRow: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    flexWrap: "wrap",
  },
  oldPriceLabelText: {
    fontSize: 10,
    fontWeight: 700,
    color: "#8a8278",
    textTransform: "uppercase",
    letterSpacing: 0.12,
  },
  oldPriceValue: {
    fontSize: 11,
    fontWeight: 500,
    color: "#a49c92",
    textDecoration: "line-through",
  },
  currentPriceRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    minHeight: 38,

  },
  oldPriceInline: {
    fontSize: 10,
    fontWeight: 700,
    color: "#8a8278",
    textTransform: "uppercase",
    letterSpacing: 0.08,
    whiteSpace: "nowrap",
  },
  memberPriceInline: {
  fontSize: 10,
  fontWeight: 700,
  color: "#8b6a2e",
  whiteSpace: "nowrap",
  lineHeight: 1.1,
},
  priceMain: {
    fontSize: "1.06rem",
    fontWeight: 600,
    lineHeight: 1.08,
    color: "#26211b",
    letterSpacing: 0,
  },
  savingsTag: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 20,
    padding: "3px 7px",
    borderRadius: 999,
    background: "rgba(212,175,55,0.09)",
    color: "#9a7720",
    fontSize: 10,
    fontWeight: 700,
    border: "1px solid rgba(212,175,55,0.16)",
    boxShadow: "none",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  unitPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: 999,
    background: "#f8f4ec",
    color: "#776446",
    fontSize: 10,
    fontWeight: 600,
    margin: 0,
  },

  /**
   * ============================================================
   * UPSELL MEMBRE
   * ============================================================
   */
  upsellBox: {
    marginTop: 2,
    padding: 10,
    background: "linear-gradient(180deg, #fffaf0, #fff7e6)",
    borderRadius: 10,
    fontSize: "0.82em",
    border: "1px solid rgba(212,175,55,0.18)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
  },
  upsellTopLine: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  lockIcon: {
    fontSize: 12,
  },
  upsellLabel: {
    color: "#7b6120",
    fontWeight: 700,
    fontSize: 11,
  },
  upsellPrice: {
    color: "#2a2a2a",
    fontSize: 11,
  },
  upsellText: {
    margin: "5px 0 0",
    color: "#8b6a2e",
    fontWeight: 600,
    fontSize: 11,
    lineHeight: 1.35,
  },

  /**
   * ============================================================
   * AJOUT PANIER
   * ============================================================
   *
   * On compacte :
   * - le label quantité
   * - le select
   * - le bouton
   *
   * Et on favorise une mise en ligne plus propre.
   */
  addRow: {
    display: "flex",
    alignItems: "end",
    gap: 8,
    marginTop: 4,
    flexWrap: "wrap",
  },
  qtyWrap: {
    display: "grid",
    gap: 4,
    minWidth: 0,
    flex: "1 1 124px",
  },
  qtyLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.08,
    opacity: 0.72,
    textTransform: "uppercase",
  },
  qtySelect: {
    height: 34,
    borderRadius: 11,
    border: "1px solid rgba(0,0,0,0.10)",
    padding: "0 10px",
    background: "white",
    fontSize: 12,
    fontWeight: 600,
    outline: "none",
    width: "100%",
    minWidth: 0,
    color: "#2b241d",
  },
  addBtn: {
    height: 34,
    padding: "0 12px",
    borderRadius: 11,
    fontWeight: 700,
    fontSize: 11,
    flex: "1 1 132px",
  },
  addBtnWide: {
    height: 34,
    padding: "0 12px",
    borderRadius: 11,
    fontWeight: 700,
    fontSize: 11,
    width: "100%",
  },
  addBtnAdded: {
    background: "#d4af37",
    color: "#fff",
    boxShadow: "0 6px 16px rgba(212,175,55,0.16)",
    transform: "translateY(-1px)",
  },

  /**
   * ============================================================
   * BADGE INFO
   * ============================================================
   */
  infoBadge: {
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "flex-start",
    padding: "6px 9px",
    borderRadius: 999,
    background: "#f5f3ef",
    color: "#6b655d",
    fontSize: 11,
    fontWeight: 600,
  },
};
